/**
 * PRIVA Governance — SmartEWM Configuration (Phase 2/3 + Outbound Phase 1).
 *
 * Application/domain configuration ONLY. Tenant Defaults (SAP Connection /
 * Warehouse / Plant) live under My Profile → SmartEWM User Context; the agent
 * inherits them at runtime. Each operational domain is a self-contained card
 * with a compact summary + "View Details" expander. Inbound and Outbound are
 * the two active domains; both follow the identical structure.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Loader2, ShieldCheck, Save, Boxes, ChevronDown, ChevronRight } from "lucide-react";
import {
  getInboundConfig, putInboundConfig,
  getOutboundConfig, putOutboundConfig, extractError,
} from "../lib/privaAdminApi";
import ClientSystemPicker from "./ClientSystemPicker";

export default function SmartEWMConfigPage() {
  const [ctx, setCtx] = useState({ clientId: "", clientSapId: "" });

  return (
    <div className="p-6 max-w-[920px] mx-auto" data-testid="smartewm-config-page">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">PRIVA Governance · Application Configuration</p>
          </div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>SmartEWM Configuration</h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            SmartEWM application and domain configuration. SAP Connection,
            Warehouse and Plant are inherited from User Context / Tenant Defaults.
          </p>
        </div>
        <ClientSystemPicker mode="client" value={ctx} onChange={setCtx} testIdPrefix="smartewm-config" />
      </header>

      {ctx.clientId ? (
        <Card title="Operational Domains" icon={Boxes} testId="smartewm-operational-domains">
          <div className="space-y-4">
            <InboundCard ctx={ctx} />
            <OutboundCard ctx={ctx} />
          </div>
        </Card>
      ) : (
        <p className="text-xs text-[#9CA3AF]">Select a business client to configure SmartEWM.</p>
      )}
    </div>
  );
}

/* ---- Inbound domain card ---- */
function InboundCard({ ctx }) {
  const d = useDomainConfig(ctx, getInboundConfig, putInboundConfig, "Inbound Domain");
  const { cfg } = d;
  const wptCount = listLen(cfg?.allowedInboundWPTs);
  const grZoneCount = listLen(cfg?.inboundGRBins);

  return (
    <DomainCard
      d={d}
      slug="inbound"
      title="Inbound"
      description="Inbound delivery, putaway execution, and GR-zone aging."
      summary={[`${wptCount} WPTs configured`, `${grZoneCount} GR zones configured`]}
    >
      {cfg && (<>
      <ApiSection letter="A" slug="inbound" title="Inbound Delivery Signal"
        service="API_WHSE_INBOUND_DELIVERY" entity="WhseInboundDeliveryItem">
        <FieldCol label="Allowed Inbound WPTs">
          <Input className="h-8 text-xs" value={csv(cfg.allowedInboundWPTs)} onChange={(e) => d.set("allowedInboundWPTs", e.target.value.split(","))} data-testid="inbound-wpts" />
        </FieldCol>
        <ReadOnlyNote label="Incomplete delivery logic" value="A delivery is flagged when Completion, Putaway or Goods-Receipt status is not fully complete (status ≠ 9)." />
        <ReadOnlyNote label="Status fields used" value="CompletionStatus · PutawayStatus · GoodsReceiptStatus · WarehouseProcessType" />
      </ApiSection>

      <ApiSection letter="B" slug="inbound" title="Inbound Warehouse Task Signal"
        service="API_WAREHOUSE_ORDER_TASK" entity="WarehouseTask">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <FieldCol label="Allowed Inbound WPTs"><Input className="h-8 text-xs" value={csv(cfg.allowedInboundWPTs)} onChange={(e) => d.set("allowedInboundWPTs", e.target.value.split(","))} data-testid="inbound-wpts-task" /></FieldCol>
          <FieldCol label="GR Storage Types"><Input className="h-8 text-xs" value={csv(cfg.inboundGRStorageTypes)} onChange={(e) => d.set("inboundGRStorageTypes", e.target.value.split(","))} data-testid="inbound-gr-types" /></FieldCol>
          <FieldCol label="GR Bins"><Input className="h-8 text-xs" value={csv(cfg.inboundGRBins)} onChange={(e) => d.set("inboundGRBins", e.target.value.split(","))} data-testid="inbound-gr-bins" /></FieldCol>
          <FieldCol label="Open Putaway Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.openPutawayAgingThresholdMinutes} onChange={(e) => d.set("openPutawayAgingThresholdMinutes", e.target.value)} data-testid="inbound-open-aging" /></FieldCol>
          <FieldCol label="In-Process Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.inProcessPutawayAgingThresholdMinutes} onChange={(e) => d.set("inProcessPutawayAgingThresholdMinutes", e.target.value)} data-testid="inbound-inproc-aging" /></FieldCol>
          <FieldCol label="GR Zone Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.grZoneAgingThresholdMinutes} onChange={(e) => d.set("grZoneAgingThresholdMinutes", e.target.value)} data-testid="inbound-gr-aging" /></FieldCol>
        </div>
        <div className="flex items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-xs text-[#374151]"><Switch checked={!!cfg.lockedWTAttentionFlag} onCheckedChange={(v) => d.set("lockedWTAttentionFlag", v)} data-testid="inbound-locked-flag" /> Flag Locked WT</label>
          <label className="flex items-center gap-2 text-xs text-[#374151]"><Switch checked={!!cfg.includeCanceledWTsInRisk} onCheckedChange={(v) => d.set("includeCanceledWTsInRisk", v)} data-testid="inbound-canceled-flag" /> Include Cancelled WT</label>
        </div>
      </ApiSection>
      </>)}
    </DomainCard>
  );
}

/* ---- Outbound domain card (mirrors Inbound) ---- */
function OutboundCard({ ctx }) {
  const d = useDomainConfig(ctx, getOutboundConfig, putOutboundConfig, "Outbound Domain");
  const { cfg } = d;
  const wptCount = listLen(cfg?.allowedOutboundWPTs);
  const stagingCount = listLen(cfg?.outboundStagingBins);

  return (
    <DomainCard
      d={d}
      slug="outbound"
      title="Outbound"
      description="Outbound delivery, picking execution, and staging/loading aging."
      summary={[`${wptCount} WPTs configured`, `${stagingCount} staging zones configured`]}
    >
      {cfg && (<>
      <ApiSection letter="A" slug="outbound" title="Outbound Delivery Signal"
        service="API_WHSE_OUTB_DLV_ORDER" entity="WhseOutboundDeliveryOrderItem">
        <FieldCol label="Allowed Outbound WPTs">
          <Input className="h-8 text-xs" value={csv(cfg.allowedOutboundWPTs)} onChange={(e) => d.set("allowedOutboundWPTs", e.target.value.split(","))} data-testid="outbound-wpts" />
        </FieldCol>
        <ReadOnlyNote label="Completion logic" value="A delivery is flagged when Goods Issue, Picking or Completion Status is not complete (status ≠ 9)." />
        <ReadOnlyNote label="Status fields used" value="GoodsIssueStatus · PickingStatus · PlannedPickingStatus · CompletionStatus · WarehouseProcessType" />
      </ApiSection>

      <ApiSection letter="B" slug="outbound" title="Outbound Warehouse Task Signal"
        service="API_WAREHOUSE_ORDER_TASK" entity="WarehouseTask">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <FieldCol label="Allowed Outbound WPTs"><Input className="h-8 text-xs" value={csv(cfg.allowedOutboundWPTs)} onChange={(e) => d.set("allowedOutboundWPTs", e.target.value.split(","))} data-testid="outbound-wpts-task" /></FieldCol>
          <FieldCol label="Allowed Staging Storage Types"><Input className="h-8 text-xs" value={csv(cfg.outboundStagingStorageTypes)} onChange={(e) => d.set("outboundStagingStorageTypes", e.target.value.split(","))} data-testid="outbound-staging-types" /></FieldCol>
          <FieldCol label="Allowed Staging Bins"><Input className="h-8 text-xs" value={csv(cfg.outboundStagingBins)} onChange={(e) => d.set("outboundStagingBins", e.target.value.split(","))} data-testid="outbound-staging-bins" /></FieldCol>
          <FieldCol label="Open Picking Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.openPickingAgingThresholdMinutes} onChange={(e) => d.set("openPickingAgingThresholdMinutes", e.target.value)} data-testid="outbound-open-aging" /></FieldCol>
          <FieldCol label="In-Process Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.inProcessPickingAgingThresholdMinutes} onChange={(e) => d.set("inProcessPickingAgingThresholdMinutes", e.target.value)} data-testid="outbound-inproc-aging" /></FieldCol>
          <FieldCol label="Loading Aging (min)"><Input type="number" className="h-8 text-xs" value={cfg.loadingAgingThresholdMinutes} onChange={(e) => d.set("loadingAgingThresholdMinutes", e.target.value)} data-testid="outbound-loading-aging" /></FieldCol>
        </div>
        <div className="flex items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-xs text-[#374151]"><Switch checked={!!cfg.lockedWTAttentionFlag} onCheckedChange={(v) => d.set("lockedWTAttentionFlag", v)} data-testid="outbound-locked-flag" /> Flag Locked WT</label>
          <label className="flex items-center gap-2 text-xs text-[#374151]"><Switch checked={!!cfg.includeCanceledWTsInRisk} onCheckedChange={(v) => d.set("includeCanceledWTsInRisk", v)} data-testid="outbound-canceled-flag" /> Include Cancelled WT</label>
        </div>
      </ApiSection>
      </>)}
    </DomainCard>
  );
}

/* ---- shared domain config hook ---- */
function useDomainConfig(ctx, getFn, putFn, label) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCfg(null);
    getFn(ctx.clientId, ctx.clientSapId)
      .then((c) => { if (!cancelled) setCfg(c); })
      .catch((e) => toast.error(extractError(e, `Failed to load ${label} config.`)));
    return () => { cancelled = true; };
  }, [ctx.clientId, ctx.clientSapId, getFn, label]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const saved = await putFn(ctx.clientId, cfg, ctx.clientSapId);
      setCfg(saved);
      toast.success(`${label} configuration saved.`);
    } catch (e) {
      toast.error(extractError(e, "Save failed."));
    } finally { setSaving(false); }
  };
  return { cfg, saving, expanded, setExpanded, set, save };
}

/* ---- shared domain card shell ---- */
function DomainCard({ d, slug, title, description, summary, children }) {
  const { cfg, saving, expanded, setExpanded, set, save } = d;
  return (
    <div className="border border-[#E5E7EB] rounded-sm p-4" data-testid={`${slug}-config-card`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Active</span>
        </div>
        {cfg && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-[#374151]">
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`${slug}-enabled-switch`} />
              {cfg.enabled ? "Enabled" : "Disabled"}
            </label>
            <SaveBtn saving={saving} onClick={save} testId={`${slug}-save-btn`} />
          </div>
        )}
      </div>
      <p className="text-xs text-[#6B7280] mb-4">{description}</p>

      {!cfg ? <Loading /> : (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid={`${slug}-summary`}>
            <SummaryChip label="2 API signals active" />
            {summary.map((s) => <SummaryChip key={s} label={s} />)}
            <SummaryChip label="Aging thresholds configured" />
          </div>
          <button type="button" onClick={() => setExpanded((e) => !e)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#025979] hover:underline"
            data-testid={`${slug}-view-details-btn`}>
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {expanded ? "Hide Details" : "View Details"}
          </button>
          {expanded && (
            <div className="mt-4 space-y-5" data-testid={`${slug}-detail-section`}>
              {children}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---- primitives ---- */
const listLen = (a) => (Array.isArray(a) ? a.filter(Boolean).length : 0);
const csv = (a) => (Array.isArray(a) ? a.join(", ") : (a || ""));

function ApiSection({ letter, slug, title, service, entity, children }) {
  return (
    <div className="border border-[#E5E7EB] rounded-sm p-4 bg-[#F8FAFC]/40" data-testid={`${slug}-api-section-${letter.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-5 h-5 rounded-full bg-[#025979] text-white text-[10px] font-bold flex items-center justify-center">{letter}</span>
        <h4 className="text-sm font-semibold text-[#111827]">{title}</h4>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-0.5 mb-3 text-[11px] text-[#6B7280]">
        <span>Service: <span className="font-mono text-[#374151]">{service}</span></span>
        <span>Entity: <span className="font-mono text-[#374151]">{entity}</span></span>
      </div>
      {children}
    </div>
  );
}
function SummaryChip({ label }) {
  return <span className="inline-flex items-center text-[11px] text-[#374151] bg-[#F1F5F9] border border-[#E5E7EB] rounded-full px-2.5 py-1">{label}</span>;
}
function ReadOnlyNote({ label, value }) {
  return (
    <div className="mt-3">
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5">{label}</p>
      <p className="text-xs text-[#374151]">{value}</p>
    </div>
  );
}
function Card({ title, icon: Icon, testId, children }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-[#111827] flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          {Icon && <Icon className="w-4 h-4 text-[#025979]" />} {title}
        </h2>
      </div>
      {children}
    </div>
  );
}
function SaveBtn({ saving, onClick, testId }) {
  return (
    <Button onClick={onClick} disabled={saving} className="h-8 text-xs bg-[#025979] hover:bg-[#013a52]" data-testid={testId}>
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
    </Button>
  );
}
function Loading() {
  return <div className="py-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#025979]" /></div>;
}
function FieldCol({ label, children }) {
  return <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">{label}{children}</label>;
}
